/**
 * Page Transition Component
 * 
 * Smooth page navigation animations
 * 
 * Version: 6.2.0
 */
import React, { useState, useEffect, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import './PageTransition.css'

type TransitionType = 'fade' | 'slide' | 'slideUp' | 'scale' | 'none'

interface PageTransitionProps {
  children: ReactNode
  type?: TransitionType
  duration?: number
  className?: string
}

const PageTransition: React.FC<PageTransitionProps> = ({
  children,
  type = 'fade',
  duration = 300,
  className = ''
}) => {
  const [isAnimating, setIsAnimating] = useState(false)
  const [showContent, setShowContent] = useState(true)
  const location = useLocation()

  useEffect(() => {
    // Start enter animation
    setIsAnimating(true)
    setShowContent(true)

    // Complete animation
    const timer = setTimeout(() => {
      setIsAnimating(false)
    }, duration)

    return () => clearTimeout(timer)
  }, [location.pathname, duration])

  const transitionClass = `page-transition-${type} ${isAnimating ? 'animating' : ''} ${className}`

  return (
    <div 
      className={transitionClass}
      style={{ 
        '--transition-duration': `${duration}ms`,
        animationDuration: `${duration}ms`
      } as React.CSSProperties}
    >
      {showContent && children}
    </div>
  )
}

// Staggered children animation wrapper
interface StaggeredListProps {
  children: ReactNode[]
  staggerDelay?: number
  className?: string
}

export const StaggeredList: React.FC<StaggeredListProps> = ({
  children,
  staggerDelay = 50,
  className = ''
}) => {
  return (
    <div className={`staggered-list ${className}`}>
      {React.Children.map(children, (child, index) => (
        <div 
          key={index}
          className="staggered-item"
          style={{ 
            animationDelay: `${index * staggerDelay}ms`,
            '--stagger-index': index
          } as React.CSSProperties}
        >
          {child}
        </div>
      ))}
    </div>
  )
}

// Page loader overlay
interface PageLoaderProps {
  isLoading: boolean
  children: ReactNode
  minDisplayTime?: number
}

export const PageLoader: React.FC<PageLoaderProps> = ({
  isLoading,
  children,
  minDisplayTime = 500
}) => {
  const [showLoader, setShowLoader] = useState(false)
  const [startTime] = useState(Date.now())

  useEffect(() => {
    if (isLoading) {
      setShowLoader(true)
    } else {
      // Ensure minimum display time for better UX
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, minDisplayTime - elapsed)
      
      setTimeout(() => {
        setShowLoader(false)
      }, remaining)
    }
  }, [isLoading, minDisplayTime, startTime])

  return (
    <div className="page-loader-container">
      {showLoader && (
        <div className="page-loader">
          <div className="loader-bar">
            <div className="loader-progress" />
          </div>
        </div>
      )}
      <div className={`page-content ${showLoader ? 'hidden' : ''}`}>
        {children}
      </div>
    </div>
  )
}

// Transition on scroll (elements animate when in view)
interface ScrollRevealProps {
  children: ReactNode
  delay?: number
  className?: string
  direction?: 'up' | 'down' | 'left' | 'right' | 'scale'
}

export const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  delay = 0,
  className = '',
  direction = 'up'
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={`scroll-reveal ${isVisible ? 'visible' : ''} ${className}`}
      style={{ 
        transitionDelay: `${delay}ms`,
        '--reveal-direction': direction
      } as React.CSSProperties}
    >
      {children}
    </div>
  )
}

export default PageTransition